import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, FolderOpen, Loader2, Plus, Trash2, Users, X } from 'lucide-react'
import api from '../../services/api'
import DossiersPanel from '../projects/DossiersPanel.jsx'

/**
 * Teams — a company's named groups of people, each given its dossiers.
 *
 * A team is a project underneath (routers/teams.py): its people and their parts, and the dossiers
 * made in it. Within a team each person approves, is a member, or only reads; the same person can
 * approve in one team and read in another. The organisation administrator forms the teams; everyone
 * else sees the teams they are in.
 */

const PARTS = [
  ['approver', 'Approver', 'Approves, sends back and submits'],
  ['member', 'Member', 'Puts documents in and agrees a move'],
  ['viewer', 'Viewer', 'Reads the team’s dossiers, changes nothing'],
]
const PART_LABEL = Object.fromEntries(PARTS.map(([k, l]) => [k, l]))
const PART_BADGE = {
  approver: 'bg-green-50 text-green-700 border-green-200',
  member: 'bg-blue-50 text-blue-700 border-blue-200',
  viewer: 'bg-gray-50 text-gray-600 border-gray-200',
}
const errText = (e, fallback) => {
  const d = e?.response?.data?.detail
  return typeof d === 'string' ? d : fallback
}

function Notice({ notice, onClose }) {
  if (!notice) return null
  return (
    <div className={`rounded-lg border px-4 py-2.5 text-sm flex items-center justify-between ${notice.kind === 'error'
      ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-800'}`}>
      {notice.text}
      <button onClick={onClose}><X className="w-4 h-4" /></button>
    </div>
  )
}

export default function Teams() {
  const [data, setData] = useState(null)
  const [notice, setNotice] = useState(null)
  const [adding, setAdding] = useState(null)        // { name, description } while forming a team
  const [busy, setBusy] = useState(false)

  const load = () => api.get('/teams/').then(r => setData(r.data))
    .catch(e => setNotice({ kind: 'error', text: errText(e, 'Could not load the teams.') }))
  useEffect(() => { load() }, [])

  const create = async () => {
    setBusy(true); setNotice(null)
    try {
      const r = await api.post('/teams/', adding)
      setNotice({ kind: 'ok', text: r.data?.detail || 'Team created.' })
      setAdding(null)
      await load()
    } catch (e) {
      setNotice({ kind: 'error', text: errText(e, 'Could not create the team.') })
    } finally { setBusy(false) }
  }

  if (!data) {
    return notice ? <div className="p-6"><Notice notice={notice} onClose={() => setNotice(null)} /></div>
      : <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Users className="w-6 h-6 text-blue-600" /> Teams</h1>
          <p className="text-sm text-gray-500 mt-1">
            {data.can_manage
              ? 'Each team has its people and its dossiers. A dossier made in a team is given to everyone in it, each in their part.'
              : 'The teams you are in, and the dossiers given to them.'}
          </p>
        </div>
        {data.can_manage && !adding && (
          <button onClick={() => { setNotice(null); setAdding({ name: '', description: '' }) }}
                  className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
            <Plus className="w-4 h-4" /> New team
          </button>
        )}
      </div>

      <Notice notice={notice} onClose={() => setNotice(null)} />

      {adding && (
        <div className="bg-white border border-blue-200 rounded-xl p-4 space-y-3">
          <label className="block">
            <span className="text-xs font-medium text-gray-600">Team name</span>
            <input value={adding.name} onChange={e => setAdding(a => ({ ...a, name: e.target.value }))} autoFocus
                   className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="e.g. Quality team" />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-600">What it looks after (optional)</span>
            <input value={adding.description} onChange={e => setAdding(a => ({ ...a, description: e.target.value }))}
                   className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          </label>
          <div className="flex justify-end gap-2">
            <button onClick={() => setAdding(null)} className="px-3 py-2 text-sm text-gray-600">Cancel</button>
            <button onClick={create} disabled={busy || !adding.name.trim()}
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40">
              Create team
            </button>
          </div>
        </div>
      )}

      {data.teams.length === 0 && (
        <div className="text-center py-14 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">{data.can_manage ? 'No teams yet. Create the first one.' : 'You are not in a team yet.'}</p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {data.teams.map(t => (
          <Link key={t.id} to={`/teams/${t.id}`}
                className="bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-sm block">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs font-mono text-gray-500">{t.number}</p>
                <p className="font-semibold text-gray-900 truncate">{t.name}</p>
                {t.description && <p className="text-xs text-gray-500 truncate">{t.description}</p>}
              </div>
              {t.my_part && (
                <span className={`text-[11px] px-2 py-0.5 rounded-full border whitespace-nowrap ${PART_BADGE[t.my_part]}`}>
                  You: {PART_LABEL[t.my_part]}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-600 mt-2">
              {PARTS.map(([k, l]) => {
                const n = t.people.filter(p => p.part === k).length
                return n ? `${n} ${l.toLowerCase()}${n === 1 ? '' : 's'}` : null
              }).filter(Boolean).join(' · ') || 'Nobody yet'}
            </p>
            <div className="mt-2 space-y-1">
              {t.dossiers.slice(0, 4).map(d => (
                <p key={d.id} className="text-xs text-gray-700 flex items-center gap-1.5 truncate">
                  <FolderOpen className="w-3.5 h-3.5 text-gray-400 shrink-0" /> {d.title}
                  <span className="text-gray-400">· {d.status}</span>
                </p>
              ))}
              {t.dossiers.length > 4 && <p className="text-xs text-gray-400">and {t.dossiers.length - 4} more</p>}
              {t.dossiers.length === 0 && <p className="text-xs text-gray-400">No dossiers yet</p>}
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}

export function TeamPage() {
  const { id } = useParams()
  const [params] = useSearchParams()
  const [team, setTeam] = useState(null)
  const [notice, setNotice] = useState(null)
  const [pick, setPick] = useState({ user_id: '', part: 'member' })
  const [busy, setBusy] = useState(null)

  const load = () => api.get(`/teams/${id}`).then(r => setTeam(r.data))
    .catch(e => setNotice({ kind: 'error', text: errText(e, 'Could not load the team.') }))
  useEffect(() => { setTeam(null); load() }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (key, fn) => {
    setBusy(key); setNotice(null)
    try {
      const r = await fn()
      setNotice({ kind: 'ok', text: r?.data?.detail || 'Done.' })
      await load()
    } catch (e) {
      setNotice({ kind: 'error', text: errText(e, 'That did not work.') })
    } finally { setBusy(null) }
  }

  if (!team) {
    return notice ? <div className="p-6"><Notice notice={notice} onClose={() => setNotice(null)} /></div>
      : <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
  }
  const inTeam = new Set(team.people.map(p => p.user_id))
  const pickable = (team.everyone || []).filter(p => !inTeam.has(p.id))

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <Link to="/teams" className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> All teams
      </Link>
      <div>
        <p className="text-xs font-mono text-gray-500">{team.number}</p>
        <h1 className="text-2xl font-bold text-gray-900">{team.name}</h1>
        {team.description && <p className="text-sm text-gray-500 mt-0.5">{team.description}</p>}
      </div>

      <Notice notice={notice} onClose={() => setNotice(null)} />

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-800">People</h2>
          <p className="text-[11px] text-gray-500">
            {PARTS.map(([, l, what]) => `${l}: ${what.toLowerCase()}`).join(' · ')}
          </p>
        </div>
        <ul className="divide-y divide-gray-100">
          {team.people.map(p => (
            <li key={p.user_id} className="px-4 py-2.5 flex flex-wrap items-center gap-3">
              <div className="min-w-0 mr-auto">
                <p className="text-sm text-gray-900">{p.name}{!p.active && <span className="text-xs text-gray-400"> · account closed</span>}</p>
                <p className="text-xs text-gray-500">{p.email}</p>
              </div>
              {team.can_manage ? (<>
                <select value={p.part} disabled={busy === `p-${p.user_id}`}
                        onChange={e => run(`p-${p.user_id}`, () => api.put(`/teams/${team.id}/people/${p.user_id}`, { part: e.target.value }))}
                        className="rounded-lg border border-gray-300 px-2 py-1 text-xs">
                  {PARTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <button title="Take them out of the team — its dossiers close to them at once"
                        onClick={() => window.confirm(`Take ${p.name} out of ${team.name}?\n\nThe team's dossiers close to them at once; what they did stays in every track record.`)
                          && run(`p-${p.user_id}`, () => api.delete(`/teams/${team.id}/people/${p.user_id}`))}
                        className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </>) : (
                <span className={`text-[11px] px-2 py-0.5 rounded-full border ${PART_BADGE[p.part]}`}>{PART_LABEL[p.part]}</span>
              )}
            </li>
          ))}
          {team.people.length === 0 && <li className="px-4 py-3 text-sm text-gray-500">Nobody in this team yet.</li>}
        </ul>
        {team.can_manage && pickable.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 bg-blue-50/40 flex flex-wrap items-center gap-2">
            <select value={pick.user_id} onChange={e => setPick(x => ({ ...x, user_id: e.target.value }))}
                    className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm min-w-[14rem]">
              <option value="">Add somebody…</option>
              {pickable.map(p => <option key={p.id} value={p.id}>{p.name} — {p.email}</option>)}
            </select>
            <select value={pick.part} onChange={e => setPick(x => ({ ...x, part: e.target.value }))}
                    className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm">
              {PARTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <button disabled={!pick.user_id || busy === 'add'}
                    onClick={async () => { await run('add', () => api.put(`/teams/${team.id}/people/${pick.user_id}`, { part: pick.part })); setPick({ user_id: '', part: 'member' }) }}
                    className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-40">
              Add to team
            </button>
          </div>
        )}
      </div>

      <DossiersPanel project={{ id: team.id, name: team.name }} inTeam
                     initialDossierId={params.get('dossier') ? Number(params.get('dossier')) : null} />
    </div>
  )
}
